import { create } from 'zustand';

/**
 * Detects if the device is mobile based on screen width and touch capability
 * @returns {boolean}
 */
function isMobileDevice() {
  const hasTouchCapability = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isNarrowScreen = window.innerWidth <= 768;
  return hasTouchCapability && isNarrowScreen;
}

const useFeedbackStore = create((set, get) => ({
  // Panel messages (history log)
  messages: [],
  seenMessages: new Set(),
  isHistoryVisible: false,

  // Contextual messages (floating at click position)
  contextualMessages: [],

  // Last click position for contextual messages
  lastClickPosition: { x: 0, y: 0 },

  // Track if click listener is initialized
  clickListenerInitialized: false,

  /**
   * Initialize global click listener to track last click position
   * Should be called once when app mounts
   */
  initClickListener: () => {
    if (get().clickListenerInitialized) return;

    const handleClick = (event) => {
      set({
        lastClickPosition: {
          x: event.clientX,
          y: event.clientY
        }
      });
    };

    // Capture phase to get position before any stopPropagation
    document.addEventListener('click', handleClick, true);
    set({ clickListenerInitialized: true });
  },

  // Panel feedback (existing functionality)
  addFeedback: (msg) => set((state) => ({
    messages: [...state.messages, msg]
  })),

  clearFeedback: () => set({ messages: [] }),

  toggleHistory: () => set((state) => ({ isHistoryVisible: !state.isHistoryVisible })),

  hasSeenMessage: (message) => {
    return get().seenMessages.has(message);
  },

  markMessageAsSeen: (message) => {
    const seen = new Set(get().seenMessages);
    seen.add(message);
    set({ seenMessages: seen });
  },

  // Contextual messages
  addContextualMessage: (msg) => {
    const { lastClickPosition } = get();
    const isMobile = isMobileDevice();

    // Apply mobile offset (60px higher to avoid finger occlusion)
    const mobileYOffset = isMobile ? 60 : 0;

    const contextualMsg = {
      ...msg,
      x: lastClickPosition.x,
      y: lastClickPosition.y - mobileYOffset,
      createdAt: Date.now()
    };

    set((state) => ({
      contextualMessages: [...state.contextualMessages, contextualMsg]
    }));

    return contextualMsg.id;
  },

  removeContextualMessage: (id) => {
    set((state) => ({
      contextualMessages: state.contextualMessages.filter(m => m.id !== id)
    }));
  },

  clearContextualMessages: () => set({ contextualMessages: [] }),
}));

export default useFeedbackStore;